import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { GiftCardsCheckBalanceAndReloadDrawer } from '@thd-olt-component-react/gift-cards-check-balance-and-reload';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { Alert } from '../../shared/alert/alert';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { PaymentsHeader } from '../payments-header/payments-header';
import { CardType } from '../../../constants/payments';
import { GiftCard } from '../gift-card/gift-card';
import { DELETE_GIFT_CARD_MSG } from '../../../constants/response-success-messages';
import { getNewPageNumber, hasNoPaymentCards } from '../../../utils/payments';
import { Pagination } from '../../shared/pagination/pagination';
import { scrollToTop } from '../../../utils/general';
import { PaymentsHeaderSecondaryButtons } from '../payments-header/payments-header-secondary-buttons';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';

export const GiftCardList: React.FC = () => {
  const [mobileDeleteIndex, setMobileDeleteIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const hasDeleteIndex = mobileDeleteIndex > -1;
  const {
    loading,
    giftCards,
    alertMessage,
    paginationInfo,
    currentPageNumber,
    getPaymentCards,
    setAlertMessage,
  } = usePayments();
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const totalPages = Number(paginationInfo?.totalPages) || 0;

  useEffect(() => {
    return () => {
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetAlert = () => {
    setAlertMessage(null);
    setMobileDeleteIndex(-1);
  };

  const alert = alertMessage && (
    <Alert status={alertMessage.alertStatus} onClose={resetAlert}>
      {alertMessage.text}
    </Alert>
  );

  const handleSuccess = (paymentId: string, message: string) => {
    resetAlert();
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.SUCCESS,
      text: message,
    });

    if (message === DELETE_GIFT_CARD_MSG.message) {
      const alertIndex = (giftCards?.paymentCards || []).findIndex(
        (gc) => gc.paymentId === paymentId,
      );
      setMobileDeleteIndex(alertIndex);
      const newPage = getNewPageNumber(
        currentPageNumber,
        totalPages,
        paginationInfo?.paymentsOnCurrentPage,
      );
      onChangePage(newPage);
      return;
    }
    onChangePage(1);
  };

  const onChangePage = (newPageNumber: number) => {
    triggerGetPaymentCards(newPageNumber);
  };

  const handleError = (paymentId: string, message: string) => {
    resetAlert();
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.ERROR,
      text: message,
    });
  };

  const triggerGetPaymentCards = (pageNumber: number) => {
    return getPaymentCards(CardType.GIFT_CARD, false, false, {
      page: pageNumber,
    });
  };

  const shouldShowInMobile =
    isMobile && (!!alertMessage?.paymentId || hasDeleteIndex);

  // general error messages are not showing for mobile users when attempting to add a gift card
  const desktopAlertMessage = alertMessage &&
    !shouldShowInMobile &&
    (alertMessage.alertStatus === TAlertStatus.ERROR ||
      alertMessage.alertStatus === TAlertStatus.SUCCESS) && (
      <div className="sui-mb-8">{alert}</div>
    );

  const giftCardMsg = (
    <Alert status="warning">
      <span className="sui-text-base sui-flex sui-flex-wrap">
        Please keep your gift card to reference the PIN number. You will need to
        reenter your PIN at checkout.
      </span>
    </Alert>
  );

  const getGiftCardList = () => {
    const giftCardList =
      giftCards?.paymentCards?.map((card) => {
        const { paymentId } = card;
        return (
          <React.Fragment key={paymentId}>
            {alertMessage &&
              shouldShowInMobile &&
              !hasDeleteIndex &&
              alertMessage.paymentId === paymentId &&
              alert}
            <GiftCard
              card={card}
              onActionSuccess={handleSuccess}
              onActionFailure={handleError}
              onCheckBalaceAndReloadDrawerOpen={() => {
                setIsOpen(true);
              }}
            />
          </React.Fragment>
        );
      }) || [];

    if (shouldShowInMobile && hasDeleteIndex && alert) {
      giftCardList.splice(mobileDeleteIndex, 0, alert);
    }

    return giftCardList;
  };

  return (
    <>
      <Spinner active={loading} />
      <PaymentsHeader
        cardType={CardType.GIFT_CARD}
        onActionFailure={handleError}
        onActionSuccess={handleSuccess}
      />
      {giftCardMsg}
      {desktopAlertMessage}
      <PaymentsHeaderSecondaryButtons
        cardType={CardType.GIFT_CARD}
        onActionSuccess={handleSuccess}
        onActionFailure={handleError}
      />
      {hasNoPaymentCards(giftCards) && (
        <NoPaymentCardsMessage tenderType={CardType.GIFT_CARD} />
      )}
      <div className="sui-grid sui-grid-cols-1 sui-gap-y-8 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3">
        {getGiftCardList()}
        <GiftCardsCheckBalanceAndReloadDrawer
          open={isOpen}
          onCancel={() => setIsOpen(false)}
        />
      </div>
      {totalPages > 1 && (
        <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
          <Pagination
            nPages={totalPages}
            onChangePage={(newPage) => {
              onChangePage(newPage);
              setAlertMessage(null);
              scrollToTop();
            }}
            page={currentPageNumber}
          />
        </div>
      )}
    </>
  );
};
